import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { clientChipField, dateField, serviceField } from '../../../../../shared/components/kit/table/table.fields';
import { RiskyClientsWidgetStore } from './risky-clients-widget.store';
import { TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { FolderRiskStatusEnum } from '@libs/types/src/services/enum/folder-risk-status.enum';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { FolderRiskStatusObject } from '@libs/types/src/services/object-type/folder/folder-risk-status.object';
import { riskyClientFrag } from './risky-clients-widget.query';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-risky-clients-widget',
    templateUrl: './risky-clients-widget.component.html',
    styleUrl: './risky-clients-widget.component.scss',
    providers: [RiskyClientsWidgetStore],
    imports: [
        TableComponent,
        PrimeTemplate,
        NgClass,
        TranslatePipe,
    ],
})
export class RiskyClientsWidgetComponent {
    protected readonly store = inject(RiskyClientsWidgetStore);

    items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);

    protected columns = computed(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        let cols = [
            clientChipField('client', 'folder.person', false),
            { name: 'riskType', type: TableColumnType.String },
            { name: 'status', type: TableColumnType.String },
            dateField('dateFrom', false, false),
            { name: 'note', type: TableColumnType.String50 },
        ];

        if (showServiceInfo) {
            cols = [
                ...cols.slice(0, 1),
                serviceField('service', 'folder.serviceId'),
                ...cols.slice(1),
            ];
        }

        return cols;
    });

    protected data = signal<FolderRiskStatusObject[]>([]);

    constructor() {
        effect(async () => {
            if (!this.items().length) {
                return;
            }

            const promises = this.items().map(async (item) => {
                const filter = new FilterBuilder()
                    .where('serviceId', item.serviceId)
                    .where('status', [FolderRiskStatusEnum.RISKY, FolderRiskStatusEnum.POSITIVE], 'IN')
                    .where('dateFromLimitDays', item.widget.days)
                    .where('latest', true)
                    .sort('dateFrom', 'DESC');
                return await this.store.api.getFolderRiskStatuses(filter.create(), riskyClientFrag);
            });

            const data = await Promise.all(promises);
            this.data.set(data.flatMap(obj => (obj?.items || []) as FolderRiskStatusObject[]));
        });
    }

    protected readonly FolderRiskStatusEnum = FolderRiskStatusEnum;
}
